import { useCallback, useEffect, useRef } from 'react';

interface UseInfiniteScrollOptions {
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  enabled?: boolean;
}

export function useInfiniteScroll({
  loading,
  hasMore,
  onLoadMore,
  enabled = true,
}: UseInfiniteScrollOptions) {
  const observer = useRef<IntersectionObserver | null>(null);
  // Use refs to always have current values in the callback
  const loadingRef = useRef(loading);
  const hasMoreRef = useRef(hasMore);
  const enabledRef = useRef(enabled);
  const onLoadMoreRef = useRef(onLoadMore);
  const lastLoadTime = useRef<number>(0);

  // Update refs when values change
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  const lastElementRef = useCallback(
    (node: HTMLElement | null) => {
      // Disconnect previous observer
      if (observer.current) observer.current.disconnect();

      // Don't observe if there's no node
      if (!node) return;

      // Create new observer with stable callback
      observer.current = new IntersectionObserver(
        entries => {
          // Use the intersection entry
          const entry = entries[0];
          if (!entry) return;

          const now = Date.now();
          const timeSinceLastLoad = now - lastLoadTime.current;

          // Check current values from refs to avoid stale closures
          if (
            entry.isIntersecting &&
            hasMoreRef.current &&
            !loadingRef.current &&
            enabledRef.current &&
            timeSinceLastLoad > 200 // Standard debounce to prevent rapid requests
          ) {
            lastLoadTime.current = now;
            onLoadMoreRef.current();
          }
        },
        {
          // Trigger immediately when any part of element appears (fixes fast scroll)
          threshold: 0,
          // Small margin for slightly earlier triggering
          rootMargin: '50px',
        }
      );

      // Start observing the node
      observer.current.observe(node);
    },
    [] // Empty dependency array to prevent unnecessary recreations
  );

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  return lastElementRef;
}
